import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { SessionUser } from '../shared/contracts';
import { api, setCsrfToken } from './api';
import { Layout } from './components/Layout';
import { PandaMark } from './components/PandaMark';
import { AdminPage } from './pages/AdminPage';
import { DashboardPage } from './pages/DashboardPage';
import { EventsPage } from './pages/EventsPage';
import { SettingsPage } from './pages/SettingsPage';
import { MailSearchPage } from './pages/MailSearchPage';
import { QuickAddPage } from './pages/QuickAddPage';
import { SourceMailPage } from './pages/SourceMailPage';
import type { SessionResponse } from './types';

function Loading() {
  return <div className="center-screen"><PandaMark size={92} animated /><p>メールを見回り中…</p></div>;
}

function Welcome({ oauthConfigured }: { oauthConfigured: boolean }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const invite = params.get('invite');
  const error = params.get('error');
  const messages: Record<string, string> = {
    oauth_state: '接続確認をやり直します。Google接続ボタンからもう一度お試しください。', oauth_expired: '接続操作の有効時間が切れました。もう一度お試しください。',
    invite_required: 'このアプリは招待制です。管理者から届いたURLを開いてください。', invite_email: '招待されたGoogleアカウントで接続してください。',
    oauth_failed: 'Googleとの接続を完了できませんでした。設定を確認して再度お試しください。',
  };
  return (
    <div className="welcome-page">
      <div className="welcome-card">
        <PandaMark size={92} animated />
        <p className="eyebrow">忙しい人の、予定の見張り番</p>
        <h1>めーるパンダ</h1>
        <p className="lead">メールに書かれた予定や締切を見つけて、専用のGoogleカレンダーへそっと入れておきます。</p>
        <div className="promise-list">
          <div><span>✓</span><p><strong>見落とさない</strong><small>迷った日付も「要確認」で残します</small></p></div>
          <div><span>✓</span><p><strong>すぐ消せる</strong><small>不要な予定は一覧からまとめて削除</small></p></div>
          <div><span>✓</span><p><strong>本文は見せない</strong><small>具体的データはあなたのGoogle Driveへ</small></p></div>
        </div>
        {error && <div className="alert error">{messages[error] ?? '接続に失敗しました。'}</div>}
        {!oauthConfigured && <div className="alert warning">現在Google OAuthの運営者設定中です。</div>}
        <a className={`button primary big ${!oauthConfigured ? 'disabled' : ''}`} href={`/api/auth/google/start${invite ? `?invite=${encodeURIComponent(invite)}` : ''}`} aria-disabled={!oauthConfigured}>Googleと接続する</a>
        <p className="scope-note">Gmailとカレンダーの読み取り、専用カレンダー・専用データファイルの作成を許可します。メール送信や他のカレンダーの変更は行いません。</p>
      </div>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    api<SessionResponse>('/api/auth/session').then((value) => {
      if (value.csrfToken) setCsrfToken(value.csrfToken);
      setSession(value);
    }).catch(() => setSession({ authenticated: false, oauthConfigured: true }));
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline); window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, []);
  if (!session) return <Loading />;
  if (!session.authenticated || !session.user) return <Welcome oauthConfigured={session.oauthConfigured} />;
  const user = session.user as SessionUser;
  return (
    <Layout user={user} online={online}>
      <Routes>
        <Route path="/" element={<DashboardPage user={user} />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/quick-add" element={<QuickAddPage />} />
        <Route path="/mail-search" element={<MailSearchPage />} />
        <Route path="/source-mail/:messageId" element={<SourceMailPage />} />
        <Route path="/settings" element={<SettingsPage user={user} onSignedOut={() => setSession({ authenticated: false, oauthConfigured: true })} />} />
        <Route path="/admin" element={user.role === 'admin' ? <AdminPage /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
